import React from 'react'

import NeatVsCompetitorCompare from '@/components/templates/NeatVsCompetitorCompare'
import trendsInsightsImg from '@/assets/track-receipts/trends-insights.png'
import allFilesImg from '@/assets/features-potential/all-files.jpg'
import autoentryLogo from '@/assets/neat-competitor/autoentry-logo.png'
import avatarImage from '@/assets/testimonials/avatar.png'

const pageId = 'neatVsAutoentry'

const overview = [
  {
    title: 'Capture & upload documents easily',
    text:
  <><p>Neat & AutoEntry allow you to track your receipts and documents from anywhere. Both software applications provide various ways to upload your documents easily and have powerful data extraction & verification technologies. Use your phone to snap an image, send by email, scanner or drag and drop your documents.</p>
    <p>AutoEntry allows you to upload up to a number of documents monthly based on the plan through their credit system. Neat provides unlimited uploads of documents with any plan.</p>
    <p>AutoEntry provides a credit system that acts as currency for uploaded items. For instance, purchase or sales invoices may use 1 credit to upload but if these invoices have line items, expect to use 2 credits. Financial documents like bank statements may use up to 3 credits. Their lowest plan, Bronze (at $12/month) provides 50 credits while their highest plans Diamond (at $285/month) and Sapphire (at $250/month) can provide 1500 or 2500 credits per month.</p>
    <p>Neat allows you to upload unlimited amounts of any type of documents such as receipts, invoices, statements, etc. With Neat, you also have unlimited file storage.</p>
  </>,
  },
  {
    title: 'Financial reporting',
    text:
  <><p>Neat provides financial reporting through an add-on plan which allows you to connect bank accounts. You can analyze your finances with live data and get real-time insights--including net cash flow, top expense categories, cash balance, profit and loss, and total sales. Automatic, real-time accounting reports such as profit and loss statements, cash flow statements, balance sheets, and transaction reports help you to make informed decisions. With document scanning and tracking, you will be able to pair receipts with expenses through guided workflows to make accounting easy.</p>
    <p>AutoEntry is only a document capture application and currently does not provide any type of reporting.</p>
  </>,
  },
  {
    title: 'Integrations',
    text:
  <><p>Both Neat & AutoEntry work with 3rd party applications.</p>
    <p>AutoEntry can integrate with Sage, Quickbooks, Xero and some others.</p>
    <p>Neat integrates with:</p>
    <ul>
      <li>Quickbooks</li>
      <li>H&R Block</li>
      <li>Quicken</li>
      <li>TurboTax</li>
      <li>Outlook</li>
      <li>Campaign Monitor</li>
      <li>Mailchimp</li>
      <li>Google Contacts</li>
      <li>Constant Contact</li>
    </ul>
  </>,
  },
]

const comparisons = [
  {
    rowTitle: 'Overview',
    neatText: 'Neat helps you scan and track receipts and documents. Neat’s powerful OCR technology extracts key information for automation. You can also monitor top expense categories, cash balances, and analyze finances with live data. Neat is a smart file organization and financial reporting tool.',
    competitorText: 'AutoEntry is an application that allows you to capture, categorize, and send to your accounting software. AutoEntry uses automated data entry to interpret and source information from your documents.',
  },
  {
    rowTitle: 'Platforms supported',
    neatText: 'Neat can be accessed anywhere, anytime as a web-based app, iOS & Android.',
    competitorText: 'Web-based, iOS & Android.',
  },
  {
    rowTitle: 'Best for',
    neatList: [
      'Accountants/Bookkeepers',
      'Small to Medium size businesses',
    ],
    competitorList: [
      'Accountants/Bookkeepers',
      'Small to Medium size businesses',
    ],
  },
  {
    rowTitle: 'Customer support',
    neatList: [
      'Chat',
      'Email',
      'Phone',
      '24/7 Knowledge Base',
      'Webinars',
    ],
    competitorList: [
      'Chat',
      'Email',
      'Phone',
      'Help Center',
      'Webinars',
    ],
  },
  {
    rowTitle: 'Features',
    neatList: [
      'Document scanning by mobile, email, drag & drop to app, or compatible scanner',
      'Unlimited file storage',
      'Organize files by expense type, tax category, and more',
      'Easily filter and search for documents',
      'Track expenses with reporting and insights capabilities built-in',
      'Real-time insights - including net cash flow, top expense categories, cash balance, profit and loss',
      'Connect to bank accounts',
      'Categorization of expenses can be done without requiring a file to be captured',
      'Bank-level data security',
    ],
    competitorList: [
      'Capture documents by mobile, email, drag & drop to app or scanner',
      'Categorize data',
      'Publish to accounting software',
    ],
  },
  {
    rowTitle: 'Integrations',
    neatList: [
      'Intuit Quickbooks',
      'Intuit Turbotax',
      'H&R Block',
      'Quicken',
      'Outlook',
      'Campaign Monitor',
      'Mailchimp',
      'Google Contacts',
      'Constant Contact',
    ],
    competitorList: [
      'Quickbooks',
      'Xero',
      'Sage',
      'Other accounting software',
    ],
  },
  {
    rowTitle: 'Pricing',
    neatList: [
      'Neat - $200/yr',
      'Neat w/ VIP Service - $250/yr',
      'Neat w/ Automated Insights (AI) - $350/yr',
      'Neat w/ AI & VIP - $400/yr',
      'Free trial available',
    ],
    competitorList: [
      'Bronze $144/yr - 50 credits/mo',
      'Silver $276/yr - 100 credits/mo',
      'Gold $528/yr - 200 credits/mo',
      'Platinum $1,176/yr - 500 credits/mo',
      'Diamond $3,420/yr - 1500 credits/mo',
      'Sapphire $5,400/yr - 2500 credits/mo',
      'Free trial available',
    ],
  },
]

const featureComparison = [
  {
    feature: 'Document scanning, storage & categorization',
    neat: true,
    competitor: true,
  },
  {
    feature: 'Bank-level security',
    neat: true,
    competitor: true,
  },
  {
    feature: 'iOS & Android app',
    neat: true,
    competitor: true,
  },
  {
    feature: 'Unlimited parsing',
    neat: true,
    competitor: false,
  },
  {
    feature: 'Scanner integration',
    neat: true,
    competitor: false,
  },
  {
    feature: 'Track expenses',
    neat: true,
    competitor: false,
  },
  {
    feature: 'Full text searchability',
    neat: true,
    competitor: false,
  },
  {
    feature: 'Advanced filtering',
    neat: true,
    competitor: false,
  },
]

const testimonialSliderProps = {
  author: 'Joann D.',
  text: 'I have been using Neat for at least 7 years, and it\'s made tracking receipts and generating year end reports a breeze.',
  avatar: avatarImage,
  slides: [
    { image: trendsInsightsImg },
    { image: allFilesImg },
  ],
}

export default function NeatVsAutoentry() {
  return (
    <NeatVsCompetitorCompare
      comparisons={comparisons}
      competitorLogo={autoentryLogo}
      competitorName="AutoEntry"
      ctaTitle="Easy and efficient - now that's Neat!"
      featureComparison={featureComparison}
      heroTitle={<>Capture your financial documents, automate & organize your data entry</>}
      overview={overview}
      pageTitle="Neat vs. AutoEntry"
      testimonialSliderProps={testimonialSliderProps}
    />
  )
}

NeatVsAutoentry.layoutProps = {
  pageId,
  showFooter: false,
  showHeader: false,
}
